import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeyToAreaMapping, useNumberedBinAreas } from "./useAreaHotkeys";

/**
 * Bin-number entry lets a labeller type a bin number to pick a numbered bin
 * when there are more bins than free single-key hotkeys (Bluescope EAF Classic
 * has 26).
 */

const TRIGGER_KEY_CODE = "KeyB";
const TRIGGER_KEY_LETTER = "b";

const digitFromCode = (code: string): number | null => {
  const match = /^(?:Digit|Numpad)(\d)$/.exec(code);
  return match ? Number(match[1]) : null;
};

export type BinEntryState = Readonly<{ active: boolean; buffer: string }>;

export type BinKeyOutcome = Readonly<{
  consumed: boolean;
  nextState: BinEntryState;
  commitAreaId?: string;
}>;

const INACTIVE: BinEntryState = { active: false, buffer: "" };

/**
 * Pure keydown transition for the bin-entry surfaces. `triggerAvailable` is
 * false when the trigger key already selects an area in this layout, or there
 * are no numbered bins, so it keeps its normal meaning.
 *
 * `eager` distinguishes the two surfaces:
 * - Modal (`false`): an ambiguous prefix (`1` when 10-19 exist) waits for a
 *   further digit or Enter, because committing closes the modal.
 * - Alt overlay (`true`): every valid bin commits immediately and the buffer
 *   stays open so a further digit refines it (1 -> 13). The overlay closes on
 *   Alt-release, so a lone ambiguous digit still records with real duration.
 */
export const reduceBinEntryKey = (
  state: BinEntryState,
  code: string,
  binAreasByNumber: ReadonlyMap<number, string>,
  triggerAvailable: boolean,
  eager = false
): BinKeyOutcome => {
  if (!state.active) {
    if (triggerAvailable && code === TRIGGER_KEY_CODE) {
      return { consumed: true, nextState: { active: true, buffer: "" } };
    }
    return { consumed: false, nextState: state };
  }

  // Don't consume Escape — the modal should still close on it.
  if (code === "Escape") {
    return { consumed: false, nextState: state };
  }
  if (code === "Backspace") {
    const buffer = state.buffer.slice(0, -1);
    // Eager mode's selection follows the buffer, so shrinking it re-selects
    // the shorter bin.
    const areaId =
      eager && buffer !== "" ? binAreasByNumber.get(Number(buffer)) : undefined;
    return areaId === undefined
      ? { consumed: true, nextState: { active: true, buffer } }
      : {
          consumed: true,
          nextState: { active: true, buffer },
          commitAreaId: areaId,
        };
  }
  if (code === "Enter" || code === "NumpadEnter") {
    // Eager mode already committed each prefix, so Enter does nothing.
    if (eager) {
      return { consumed: true, nextState: state };
    }
    const typed = Number(state.buffer);
    const areaId =
      state.buffer !== "" ? binAreasByNumber.get(typed) : undefined;
    return areaId === undefined
      ? { consumed: true, nextState: state }
      : { consumed: true, nextState: INACTIVE, commitAreaId: areaId };
  }

  const digit = digitFromCode(code);
  if (digit === null) {
    return { consumed: true, nextState: state };
  }

  const candidate = state.buffer + String(digit);
  const candidateNumber = Number(candidate);
  const candidateAreaId = binAreasByNumber.get(candidateNumber);
  const hasLongerValidNumber = [...binAreasByNumber.keys()].some(
    (binNumber) => {
      const asString = String(binNumber);
      return (
        asString.length > candidate.length && asString.startsWith(candidate)
      );
    }
  );

  if (candidateAreaId === undefined) {
    return hasLongerValidNumber
      ? { consumed: true, nextState: { active: true, buffer: candidate } }
      : { consumed: true, nextState: state };
  }

  if (eager) {
    return {
      consumed: true,
      nextState: { active: true, buffer: candidate },
      commitAreaId: candidateAreaId,
    };
  }
  return hasLongerValidNumber
    ? { consumed: true, nextState: { active: true, buffer: candidate } }
    : { consumed: true, nextState: INACTIVE, commitAreaId: candidateAreaId };
};

type UseBinNumberEntryArgs = Readonly<{
  location: string | undefined;
  enabled: boolean;
  onSelectBin: (areaId: string) => void;
}>;

type UseBinNumberEntryResult = Readonly<{
  binEntryActive: boolean;
  binBuffer: string;
  binEntryAvailable: boolean;
  handleKeyDown: (event: KeyboardEvent) => boolean;
}>;

export const useBinNumberEntry = ({
  location,
  enabled,
  onSelectBin,
}: UseBinNumberEntryArgs): UseBinNumberEntryResult => {
  const binAreasByNumber = useNumberedBinAreas(location);
  const hotkeyToArea = useHotkeyToAreaMapping(location);
  const triggerAvailable =
    binAreasByNumber.size > 0 && !(TRIGGER_KEY_LETTER in hotkeyToArea);

  const [binEntryState, setBinEntryState] = useState<BinEntryState>(INACTIVE);

  // Reset on open/close at render time (not an effect) so no stale buffer carries over.
  const [wasEnabled, setWasEnabled] = useState(enabled);
  if (wasEnabled !== enabled) {
    setWasEnabled(enabled);
    if (binEntryState !== INACTIVE) {
      setBinEntryState(INACTIVE);
    }
  }

  const stateRef = useRef(binEntryState);
  useEffect(() => {
    stateRef.current = binEntryState;
  }, [binEntryState]);
  const binAreasRef = useRef(binAreasByNumber);
  useEffect(() => {
    binAreasRef.current = binAreasByNumber;
  }, [binAreasByNumber]);
  const triggerAvailableRef = useRef(triggerAvailable);
  useEffect(() => {
    triggerAvailableRef.current = triggerAvailable;
  }, [triggerAvailable]);
  const onSelectBinRef = useRef(onSelectBin);
  useEffect(() => {
    onSelectBinRef.current = onSelectBin;
  }, [onSelectBin]);

  const handleKeyDown = useCallback((event: KeyboardEvent): boolean => {
    const outcome = reduceBinEntryKey(
      stateRef.current,
      event.code,
      binAreasRef.current,
      triggerAvailableRef.current
    );
    if (!outcome.consumed) {
      return false;
    }
    event.preventDefault();
    event.stopPropagation();
    if (outcome.nextState !== stateRef.current) {
      stateRef.current = outcome.nextState;
      setBinEntryState(outcome.nextState);
    }
    if (outcome.commitAreaId !== undefined) {
      onSelectBinRef.current(outcome.commitAreaId);
    }
    return true;
  }, []);

  return {
    binEntryActive: binEntryState.active,
    binBuffer: binEntryState.buffer,
    binEntryAvailable: triggerAvailable,
    handleKeyDown,
  };
};
